"use client"

import { useState, useEffect } from "react"
import { getAuth } from "firebase/auth"
import { getDatabase, ref as dbRef, push, onValue,runTransaction } from "firebase/database"
import "./RaiseIssuePage.css"
import { useLocation } from "react-router-dom"
import { FaInfoCircle } from "react-icons/fa"
import { db, ref, get, set } from "../Firebase/firebase-config"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"


const MAX_ISSUES_PER_DAY = 10;
const RaiseIssuePage = () => {
  const location = useLocation()
  const orderDetails = location.state || {}
  const { userId } = useGetCurrentUserId()

  const [issueType, setIssueType] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState("")
  const [attachment, setAttachment] = useState(null)
  const [contactInfo, setContactInfo] = useState({ name: "", email: "" })
  const [confirmation, setConfirmation] = useState(false)
  const [issuesHistory, setIssuesHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [companyName, setCompanyName] = useState("")
  const [siteAddress, setSiteAddress] = useState(orderDetails.siteAddress || "")
  const [brand, setBrand] = useState(orderDetails.brand || "")
  const [grade, setGrade] = useState(orderDetails.grade || "")
  const [dispatchDetails, setDispatchDetails] = useState(orderDetails.dispatchDetails || "")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [showIssues, setShowIssues] = useState(false)

  // ─── Daily limit states ───
  const [remainingIssues, setRemainingIssues] = useState(MAX_ISSUES_PER_DAY)

  const getTodayDateKey = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000
    const istDate = new Date(now.getTime() + istOffset)
    istDate.setHours(0, 0, 0, 0)
    return istDate.toISOString().split("T")[0] // "2025-03-13"
  }

  // Listen to remaining count in real-time
  useEffect(() => {
    if (!userId) return

    const today = getTodayDateKey()
    const countRef = dbRef(getDatabase(), `limit_counter/daily_issues_counts/${userId}/${today}`)

    const unsubscribe = onValue(countRef, (snapshot) => {
      const count = snapshot.val() || 0
      setRemainingIssues(Math.max(0, MAX_ISSUES_PER_DAY - count))
    })

    return () => unsubscribe()
  }, [userId])

  useEffect(() => {
    const auth = getAuth()
    const user = auth.currentUser
    if (user) {
      setContactInfo({
        name: user.username || "",
        email: user.email || "",
      })
    }
  }, [])

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      try {
        const profileRef = ref(db, `profileDashboard/${userId}`)
        const snapshot = await get(profileRef)
        if (snapshot.exists()) {
          const profileData = snapshot.val()
          setCompanyName(profileData.company_name || "Unknown Company")
        } else {
          setError("Company name not found in profileDashboard.")
        }
      } catch (err) {
        console.error("Error fetching user data:", err)
        setError("Error fetching company information")
      }
    }
    fetchUserData()
  }, [userId])

  useEffect(() => {
    if (orderDetails) {
      setBrand(orderDetails.brand || "")
      setGrade(orderDetails.grade || "")
      setSiteAddress(orderDetails.siteAddress || "")
      setDispatchDetails(orderDetails.dispatchDetails || "")
    }
  }, [orderDetails])

  useEffect(() => {
    const fetchIssueHistory = () => {
      if (!userId) {
        setLoading(false)
        return
      }

      const database = getDatabase()
      const issuesRef = dbRef(database, `issues/${userId}`)
      const unsubscribe = onValue(issuesRef, (snapshot) => {
        const data = snapshot.val()
        const issues = data ? Object.values(data) : []
        setIssuesHistory(issues)
        setLoading(false)
      })

      return unsubscribe
    }

    const unsub = fetchIssueHistory()
    return () => {
      if (unsub) unsub()
    }
  }, [userId])

  const getIndianTimestamp = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000
    const istTime = new Date(now.getTime() + istOffset)
    return istTime.toISOString().replace("T", " ").replace("Z", "")
  }

  const sendNotification = async (issuesId, companyName) => {
    if (!userId) return

    const notificationRef = ref(db, `supplier_notifications/Raised Issues/${userId}/${issuesId}`)
    const gifUrl = "Images/New notification.gif"

    const newNotification = {
      title: "Issues",
      brand,
      siteAddress,
      issueType,
      message: `A new issue has been sent by ${companyName}. Please review the issue.`,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    }

    try {
      await set(notificationRef, newNotification)
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // 1. Check daily limit first
    if (remainingIssues <= 0) {
      setError("You have reached the daily limit of 10 issues. Please try again tomorrow.")
      return
    }

    const cleaned = description.replace(/\s/g, "")
    if (cleaned.length < 10 || cleaned.length > 100) {
      setError("Description must be between 10 and 100 characters.")
      return
    }

    if (!userId) {
      setError("User not authenticated")
      return
    }

    if (issueType && description && brand && grade) {
      const database = getDatabase()
      const issuesRef = dbRef(database, `issues/${userId}`)
      // 3. Atomic increment of counter using transaction
      const today = getTodayDateKey()
      const countRef = dbRef(database, `limit_counter/daily_issues_counts/${userId}/${today}`)

      await runTransaction(countRef, (current) => {
        const cnt = current || 0
        if (cnt >= MAX_ISSUES_PER_DAY) {
          throw new Error("daily_limit_reached")
        }
        return cnt + 1
      })
      const newIssue = {
        issueType,
        description,
        brand,
        companyName,
        grade,
        date: new Date().toLocaleString("en-In", { timeZone: "Asia/Kolkata" }),
        status: "Pending",
        contactInfo,
        siteAddress,
        dispatchDetails,
      }
      try {
        const newIssueRef = await push(issuesRef, newIssue)
        const issuesId = newIssueRef.key

        if (issuesId) {
          await sendNotification(issuesId, companyName)
        }

        setConfirmation(true)
        setIssueType("")
        setDescription("")
        setBrand("")
        setGrade("")
        setSiteAddress("")
        setDispatchDetails("")
        setAttachment(null)

        setSuccess(true)
        setTimeout(() => {
          setSuccess(false)
          setConfirmation(false)
        }, 2000)
      } catch (error) {
        console.error("Error submitting issue:", error)
        setError("Error submitting issue")
      }
    }
  }

  const toggleIssuesHistory = () => {
    setShowIssues((prev) => !prev)
  }

  const issueInfo = () => {
    alert(
      "This page allows you to submit various types of issues, such as technical problems or billing concerns, etc. You can also view your issues response in previous Issues.",
    )
  }

  // ─── UI: Show remaining count above or below button ───
  const renderLimitInfo = () => (
    <div
      style={{
        margin: "1.5rem auto",
        padding: "1rem 1.5rem",
        maxWidth: "500px",
        backgroundColor: remainingIssues <= 2 ? "#fff3cd" : "#f8f9fa",
        borderRadius: "12px",
        textAlign: "center",
        fontSize: "0.98rem",
        fontWeight: remainingIssues <= 2 ? "600" : "500",
        color: remainingIssues <= 2 ? "#c62828" : "#424242",
        border: remainingIssues <= 2 ? "1px solid #ef9a9a" : "1px solid #e0e0e0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {remainingIssues > 0 ? (
        <>
          You have <strong>{remainingIssues}</strong> issue submissions left today (max 10/day)
        </>
      ) : (
        <>Daily limit of 10 issues reached — please try again tomorrow</>
      )}
    </div>
  )

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    )
  }

  return (
    <div className="container-fluid raise-issue-container">
      <div className="issue-form">
        <h1 className="text-center">
          Raise an Issue{" "}
          <FaInfoCircle onClick={issueInfo} style={{ fontSize: "18px", color: "#EA8716", cursor: "pointer" }} />
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="issue-group">
            <label className="issue-label">Brand</label>
            <select className="form-control" value={brand} onChange={(e) => setBrand(e.target.value)} required>
              <option value="">Select Brand</option>
              <option value="Ultratech">Ultratech</option>
              <option value="Shree">Shree</option>
              <option value="JSW">JSW</option>
              <option value="ACC">ACC</option>
              <option value="JKSuper">JK Super</option>
              <option value="Ambuja">Ambuja</option>
            </select>
          </div>

          <div className="issue-group">
            <label className="issue-label">Grade</label>
            <select className="form-control" value={grade} onChange={(e) => setGrade(e.target.value)} required>
              <option value="">Select Grade</option>
              <option value="OPC43">OPC43</option>
              <option value="OPC53">OPC53</option>
              <option value="PSC">PSC</option>
              <option value="PPC">PPC</option>
            </select>
          </div>

          <div className="issue-group">
            <label className="issue-label">Site Address</label>
            <input
              type="text"
              className="form-control"
              value={siteAddress}
              onChange={(e) => setSiteAddress(e.target.value)}
              minLength={5}
              maxLength={150}
              required
            />
          </div>

          <div className="issue-group">
            <label className="issue-label">Dispatch Details</label>
            <input
              type="text"
              className="form-control"
              value={dispatchDetails}
              readOnly
              style={{ backgroundColor: "#f8f9fa" }}
            />
          </div>

          <div className="issue-group">
            <label className="issue-label">Issue Type</label>
            <select className="form-control" value={issueType} onChange={(e) => setIssueType(e.target.value)} required>
              <option value="">Select Issue Type</option>
              <option value="Technical">Technical Issue</option>
              <option value="Order/Delivery">Order/Delivery Issue</option>
              <option value="Payment/Billing">Payment/Billing Issue</option>
              <option value="Suggestion">Suggestion</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="issue-group">
            <label className="issue-label">Description</label>
            <textarea
              className="form-control"
              rows="4"
              placeholder="Enter description here under (10-100 characters)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              minLength={10}
              maxLength={100}
              required
            />
            <small>{description.replace(/\s/g, "").length}/100 characters</small>
          </div>
{/* limit display here – e.g. before button */}
          {renderLimitInfo()}

          {error && (
            <div className="error-message" style={{ color: "red", marginBottom: "10px" }}>
              {error}
            </div>
          )}
          {success && <div className="success-Message text-white">Issue submitted successfully!</div>}

          <button type="submit" className="btn-submit">
            Submit Issue
          </button>
        </form>
      </div>

      <div className="issue-history">
        <h2>Previous Issues</h2>
        {issuesHistory.length > 0 ? (
          issuesHistory.map((issue, index) => (
            <div className="issue-card" key={index}>
              <h3>{issue.issueType}</h3>
              <p>
                <strong>Brand:</strong> {issue.brand}
              </p>
              <p>
                <strong>Grade:</strong> {issue.grade}
              </p>
              <p>
                <strong>Date:</strong> {issue.date}
              </p>
              <p className={`status ${issue.status === "Pending" ? "" : "resolved"}`}>{issue.status}</p>
            </div>
          ))
        ) : (
          <p>No issues found.</p>
        )}
      </div>
    </div>
  )
}

export default RaiseIssuePage
